import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { money0 } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const PLAN_LABELS = {
  basic: "Básico", premium: "Premium",
  founder_basic: "Fundador Básico", founder_premium: "Fundador Premium",
};

export default function CheckoutDialog({ open, onOpenChange, initialPlan = "basic" }) {
  const [form, setForm] = useState({ client_name: "", client_email: "" });
  const [plan, setPlan] = useState(initialPlan);
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) setPlan(initialPlan); }, [open, initialPlan]);
  useEffect(() => { api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {}); }, []);

  const p = plans[plan];

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", { ...form, plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "No se pudo iniciar el pago"); setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Crea tu tarjeta XUMA</DialogTitle>
          <DialogDescription>Paga la cuota de configuración y activa tu tarjeta digital.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">Plan</Label>
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger data-testid="checkout-plan"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.keys(PLAN_LABELS).map((k) => <SelectItem key={k} value={k}>{PLAN_LABELS[k]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {p && (
            <div className="rounded-xl bg-xuma-dash p-4 flex items-center justify-between">
              <span className="text-sm text-xuma-muted">Cuota de configuración</span>
              <span className="font-heading font-extrabold text-xl text-xuma-dark">{money0(p.config)} <span className="text-sm font-normal text-xuma-muted">MXN</span></span>
            </div>
          )}
          {p && <p className="text-xs text-xuma-muted -mt-2">Luego {money0(p.monthly)} MXN/mes. La mensualidad se gestiona por separado.</p>}
          <div><Label className="mb-1.5 block">Tu nombre *</Label>
            <Input data-testid="checkout-name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></div>
          <div><Label className="mb-1.5 block">Tu correo *</Label>
            <Input data-testid="checkout-email" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} /></div>
          <Button data-testid="checkout-submit" onClick={submit} disabled={loading || !form.client_name || !form.client_email}
            className="w-full h-12 rounded-2xl bg-xuma-primary hover:bg-xuma-primaryHover text-white gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Continuar al pago
          </Button>
          <p className="text-xs text-center text-xuma-muted flex items-center justify-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" /> Pago seguro con Stripe
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
